import {
    ApplicationRoleCode,
    CorporationUserRole,
    EdgeComputeOwnerType,
    ExternalNotificationType,
    GroupType,
    ICorporationResponse,
    ICurrentUserResponse,
    IDashboardResponse,
    IEdgeDefinitionResponse,
    IEdgeImageRepositoryResponse,
    IIssueActivityUserChild,
    ILocationResponse,
    IssueStatusType,
    IUserResponse,
    LocationUserRole,
    MachineHealthEventType,
    UserCorporationChild,
    UserLocationChild,
} from '@kcftech/prometheustypescriptapiclient';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { allowedHealthRangeIssueStatuses } from 'pages/Desk/issues/issue_modals/shared';
import { useEffect, useMemo, useState } from 'react';
import { sentryAddBreadcrumb, sentryCaptureMessage } from 'utils/sentry';

/**
 * Gets the user's role for the current corporation
 */
export function getCurrentCorporationRole(
    corporations: UserCorporationChild[],
    corporationId: string | undefined
) {
    return corporations.find((c) => c.CorporationId === corporationId)
        ?.CorporationUserRole;
}

/**
 * Gets the user's role for the current location
 */
export function getCurrentLocationRole(
    locations: UserLocationChild[],
    locationId: string | undefined
) {
    return locations.find((l) => l.LocationId === locationId)?.LocationUserRole;
}

const checkIsKcf = (
    user: ICurrentUserResponse | IUserResponse | IIssueActivityUserChild | undefined
) => {
    if (!user) {
        return false;
    }
    const globalRole = (() => {
        if ('ApplicationRole' in user) {
            return user.ApplicationRole;
        }
        if ('ApplicationRoleCode' in user) {
            return user.ApplicationRoleCode;
        }
        return undefined;
    })();
    const isSaas = globalRole === ApplicationRoleCode.SystemAdmin;
    const isSentry = globalRole === ApplicationRoleCode.Sentry;
    const isSales = globalRole === ApplicationRoleCode.Sales;
    return isSaas || isSentry || isSales;
};

/**
 * Checks if the user has an admin role at the supplied corporation
 * @param user the current user
 * @param corporationId the corporation ID to check the role at
 * @returns true if the user as an admin role at the corporation, undefined otherwise
 */
const hasCorporationAdminRole = (
    user: ICurrentUserResponse | undefined,
    corporationId: string | undefined
) =>
    user?.Corporations.find(({ CorporationId }) => CorporationId === corporationId)
        ?.CorporationUserRole === CorporationUserRole.Admin;

/**
 * Checks if the user has an admin role at the supplied location
 * @param user the current user
 * @param locationId the location ID to check the role at
 * @returns true if the user as an admin role at the location, undefined otherwise
 */
const hasLocationAdminRole = (
    user: ICurrentUserResponse | undefined,
    locationId: string | undefined
) =>
    user?.Locations.find(({ LocationId }) => LocationId === locationId)
        ?.LocationUserRole === LocationUserRole.Admin;

export function getUserPermissions(
    user: ICurrentUserResponse | undefined,
    location: ILocationResponse | undefined,
    corporation: ICorporationResponse | undefined,
    { adminCorporationIds, adminLocationIds }: UseOrganizationPermissionsReturn
): PermissionsContext {
    const globalRole = user?.ApplicationRole;
    const currentLocationId = location?.Id;
    const currentCorporationId = corporation?.Id;

    const currentLocationRole = getCurrentLocationRole(
        user?.Locations ?? [],
        currentLocationId
    );
    const hasRoleAtLocation = currentLocationRole !== undefined;
    const currentCorporationRole = getCurrentCorporationRole(
        user?.Corporations ?? [],
        currentCorporationId
    );
    const hasRoleAtCorporation =
        currentCorporationRole !== undefined &&
        currentCorporationRole !== CorporationUserRole.None;

    const isSaas = globalRole === ApplicationRoleCode.SystemAdmin;
    const isSentry = globalRole === ApplicationRoleCode.Sentry;
    const isSaasOrSentry = isSaas || isSentry;
    const isKcf = checkIsKcf(user);
    const isCorporationAdmin = currentCorporationRole === CorporationUserRole.Admin;
    const isLocationAdmin = currentLocationRole === LocationUserRole.Admin;

    const isAdmin = isSaasOrSentry || isCorporationAdmin || isLocationAdmin;

    const permissionsContext: PermissionsContext = {
        canViewDebug: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canEnableFeatureFlags: true,
        canViewAssetMetadata: isSaasOrSentry || isCorporationAdmin,
        canViewAssemblyMetadata: isSaasOrSentry || isCorporationAdmin,
        canViewBaseline: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canEditBaseline: isAdmin,
        canCreateTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'IndicatorGroup' ||
                item.type === 'AnalyticModel' ||
                isCollectorDependentSystem
            ) {
                return false;
            }
            return isAdmin;
        },
        canEditTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'AnalyticModel' ||
                (item.parent !== undefined && isCollectorDependentSystem)
            ) {
                return false;
            }
            return isAdmin;
        },
        canDeleteTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'AnalyticModel' ||
                (item.parent !== undefined && isCollectorDependentSystem)
            ) {
                return false;
            }
            return isAdmin;
        },
        canCreateTreeItem: ({ treeItem, system }) =>
            isAdmin &&
            !system.IsCollectorDependentSystem &&
            treeItem.TreeItemType !== GroupType.IndicatorGroup &&
            treeItem.TreeItemType !== GroupType.AnalyticModel,
        canEditTreeItem: ({ treeItem, system }) =>
            isAdmin &&
            treeItem.TreeItemType !== GroupType.AnalyticModel &&
            (!system.IsCollectorDependentSystem ||
                treeItem.TreeItemType === GroupType.System),
        canDeleteTreeItem: ({ treeItem, system }) => {
            if (
                treeItem.TreeItemType === GroupType.System ||
                system.IsCollectorDependentSystem
            ) {
                return isSaasOrSentry;
            }
            return isAdmin && treeItem.TreeItemType !== GroupType.AnalyticModel;
        },
        canAssignModel: ({ treeItem, system }) =>
            isSaasOrSentry &&
            !system.IsCollectorDependentSystem &&
            treeItem.TreeItemType !== GroupType.AnalyticModel &&
            treeItem.TreeItemType !== GroupType.IndicatorGroup,
        canViewSettings: true,
        canViewReports: true,
        canViewGlobalReports: isSaasOrSentry, // canViewDraftIssuesReport or canViewRmaSensorReport
        canViewDemo: isSaas,
        canViewCorporationDashboards: isKcf || hasRoleAtCorporation,
        canViewLocationWorkbench:
            isKcf || hasRoleAtLocation || hasRoleAtCorporation || isCorporationAdmin,
        canViewLocationDashboards:
            isKcf || hasRoleAtLocation || hasRoleAtCorporation || isCorporationAdmin,
        canCreateDashboards: isSaasOrSentry,
        canViewDashboardActions: isSaasOrSentry,
        canEditDashboard: (dashboard: IDashboardResponse) =>
            !dashboard.IsSentryManaged || isSaasOrSentry,
        canEditDashboardPermissions: isSaasOrSentry,
        canDeleteDashboard: (dashboard: IDashboardResponse) =>
            !dashboard.IsSentryManaged || isSaasOrSentry,
        canEditPowerBiReportDashboards: isSaasOrSentry,
        canEditMonitoringPoint: isAdmin,
        canEditNodeSettings: isAdmin,
        canViewTemplates: isAdmin,
        canSetFrequentUpdateIntervals: isSaas,
        canEditIndicators: isAdmin,
        canAddIndicator: isAdmin,
        canViewModel: isSaasOrSentry,
        canEditModelInEditor: isSaas,
        canViewGlobalSearchIcon: true,
        canEditActivityEvent: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canViewUsers:
            isKcf || (isAdmin && (corporation !== undefined || location !== undefined)),
        canViewTimeRangesSettings: isSaas,
        canViewThirdPartySensors: isSaas,
        canViewSetupTools: isSaasOrSentry,
        canViewBearingsSettings: true,
        canAddBearings: isSaasOrSentry,
        canViewLocationsSettings: isSaasOrSentry,
        canViewIdentityProviders: isSaas,
        canEditTree: isAdmin,
        canEditCustomFilters: isAdmin,
        canAddSentry: isSaasOrSentry,
        canAddSales: isSaasOrSentry,
        canAddSaas: isSaas,
        canAddUsers: isAdmin,
        canEditUserProperties: isSaas,
        canAdminEditUserProperties: isSaasOrSentry,
        canEditUserPersonas: !isKcf,
        canEditUserLocationAssignment: (corporationId, locationId) =>
            isSaasOrSentry ||
            ((adminLocationIds?.has(locationId) ||
                adminCorporationIds?.has(corporationId)) ??
                false),
        canEditUserCorporationAssignment: (corporationId) =>
            isSaasOrSentry || (adminCorporationIds?.has(corporationId) ?? false),
        canEditLocationMap: isAdmin,
        canCreateCorporation: isSaas,
        canViewCorporationsSettings: isKcf,
        canEditCorporationSettings: isSaas,
        canEditCorporationIcon: isKcf,
        canEditCorporationMap:
            (isCorporationAdmin && corporation !== undefined) || isSaasOrSentry,
        canViewCorporationMap: hasRoleAtCorporation || isKcf,
        canControlOtherUsersNotificationSubscriptions: isAdmin,
        canViewHardware: true,
        canEditBaseStationName: isAdmin,
        canEditRepeaterName: isAdmin,
        canViewFirmwareVersion: isSaasOrSentry,
        canEditMetadata: isAdmin,
        canViewFileLibrary: hasRoleAtCorporation || hasRoleAtLocation || isKcf,
        canUploadToFileLibrary: isAdmin,
        canEditFileLibrary: isAdmin,
        canDeleteFromFileLibrary: isAdmin,
        canEditAssetDetails: isAdmin,
        canSetSystemVisibility: isSaasOrSentry,
        canCreateSystem: isAdmin,
        canEditSystem: isAdmin,
        canDeleteSystem: isSaasOrSentry,
        canCreateDynamicSystem: isSaasOrSentry,
        canEditFileDetails: isAdmin,
        canUnlockUser: isSaas,
        canEditLocationDetails: isSaas,
        canEditLocationSubscriptionService: isSaasOrSentry,
        canViewLocationDetails: isSaasOrSentry,
        canEditLocationValueUnit: isSaas,
        canViewAssetTypeSettings: isSaasOrSentry,
        canEditAssetType: isSaas,
        canViewAssemblyTypeSettings: isSaasOrSentry,
        canEditAssemblyType: isSaas,
        canViewHardwareAdmin: isSaasOrSentry,
        canViewHardwareRelatedIssuesAcrossLocations: isSaas,
        canCreateHardware: isSaas,
        canEditNodeStatus: isAdmin,
        canSetNodeInactive: isSaas,
        canEditAllowedFrequencies: isSaas,
        canSendExternalNotifications:
            location?.Settings.ExternalNotificationType ===
            ExternalNotificationType.Manual,
        canViewDraftIssuesReport: isSaasOrSentry,
        canViewRmaSensorReport: isKcf,
        canEditScheduledReportSubscribers: isAdmin,
        canDeleteScheduledReportSubscription: isAdmin,
        canAddKcfToNotificationSubscriptions: isSaasOrSentry,
        canViewFaultyHardwareReport: isKcf,
        canViewNotificationSubscriptionsSettings: true,
        canViewGroupNotificationSubscriptions: isAdmin,
        canDeleteUserNotificationSubscription: isAdmin,
        canCreateNotificationSubscriptionsForOthers: isAdmin,
        canViewIndustries: isSaas,
        // TODO: Change to isAdmin || isKcf once we have more information on use cases
        canViewContainerManagement: isSaas,
        // TODO: Change to isAdmin once we have more information on use cases
        canEditContainerManagement: isSaas,
        canGrantAccessToAll: isSaas,
        canViewKcfUsers: isKcf,
        canEditHubName: isAdmin,
        canEditHubAdmin: isSaas,
        canViewIntegrations: isAdmin,
        canCreateIntegrations: isSaasOrSentry,

        useCanEditServiceDefinition: (definition: IEdgeDefinitionResponse | undefined) =>
            useOwnedEntityEditPermissions(
                user,
                useMemo(
                    () =>
                        definition
                            ? {
                                  id: definition.Owner.Id,
                                  type: definition.Owner.OwnerType,
                              }
                            : undefined,
                    [definition]
                )
            ),

        useCanEditImageRepository: (
            imageRepository: IEdgeImageRepositoryResponse | undefined
        ) =>
            useOwnedEntityEditPermissions(
                user,
                useMemo(
                    () =>
                        imageRepository
                            ? {
                                  id: imageRepository.Owner.OwnerId,
                                  type: imageRepository.Owner.OwnerType,
                              }
                            : undefined,
                    [imageRepository]
                )
            ),

        canViewKcfInfo: isKcf,
        canEditKcfInfo: isSaas,
        canExportChartImages: isSaasOrSentry,
        canViewWaterfallChart: true,
        canCreateCustomMarker: isAdmin,
        canViewMachineHealthNotificationSubscriptionCategory: (
            category: MachineHealthEventType
        ) => {
            if (isSaasOrSentry) {
                return true;
            }

            return (
                category !== MachineHealthEventType.DraftIssueCreate ||
                (location !== undefined &&
                    location.Settings.DraftIssuesAvailableToCustomerAdmins &&
                    isAdmin)
            );
        },
        issue: {
            canViewHardwareIssues: true,
            canViewInternalIssues: isKcf,
            canEditInternalIssue: isSaas,
            canBatchArchive: isSaasOrSentry,
            canViewDraftIssues: (mode) =>
                isSaasOrSentry ||
                (mode === 'machineHealth' &&
                    isAdmin &&
                    location !== undefined &&
                    location.Settings.DraftIssuesAvailableToCustomerAdmins),
            canEditIssue: (issue) => {
                // if saas or sentry and issue is not closed
                if (isSaasOrSentry) {
                    return [
                        IssueStatusType.Draft,
                        IssueStatusType.New,
                        IssueStatusType.Open,
                    ].includes(issue.IssueStatus.StatusType);
                }
                // if regular user and issue is open
                if (globalRole === ApplicationRoleCode.User) {
                    return issue.IssueStatus.StatusType === IssueStatusType.Open;
                }
                return false;
            },
            canEditIssueDescription: (issue) =>
                // saas or sentry and issue is draft
                isSaasOrSentry && issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canEditInitialRecommendation: (issue) =>
                // saas or sentry and issue is draft
                isSaasOrSentry && issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canEditComment: (comment) =>
                // owner of the comment
                comment.CreatedBy?.UserId ? comment.CreatedBy.UserId === user?.Id : false,
            canDeleteComment: (comment) =>
                // owner of the comment/reply
                comment.CreatedBy?.UserId ? comment.CreatedBy.UserId === user?.Id : false,
            canEditActivityChartDataAnnotations: (chartData) =>
                // saas, sentry, or owner of the chart data
                isSaasOrSentry ||
                (chartData.CreatedBy?.UserId
                    ? chartData.CreatedBy.UserId === user?.Id
                    : false),
            canDeleteChartDataFromActivity: (chartData) =>
                // owner of the chart data
                chartData.CreatedBy?.UserId
                    ? chartData.CreatedBy.UserId === user?.Id
                    : false,
            canEditInitialChartDataAnnotations: (issue) =>
                // saas or sentry and the issue hasn't been deleted (removed)
                isSaasOrSentry &&
                issue.IssueStatus.StatusType !== IssueStatusType.Removed,
            canDeleteInitialChartData: (issue) =>
                // saas or sentry and issue is draft
                isSaasOrSentry && issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canViewHealthRangeTagging: (issue) =>
                // saas or sentry and issue status in allowed list
                isSaasOrSentry &&
                allowedHealthRangeIssueStatuses.includes(issue.IssueStatus.StatusType),
            canFilterByHealthRanges: isSaasOrSentry,
            canIndicateFaultOnChartData: isSaasOrSentry,
            canVerifySavings: !isKcf,
            canSetValueTags: isKcf,
            canAutoOpenIssue: !isKcf,
        },
    };

    return permissionsContext;
}

export { checkIsKcf as isKcf };

type SimpleEntityOwner = {
    id: string;
    type: EdgeComputeOwnerType;
};

const useOwnedEntityEditPermissions = (
    user: Readonly<ICurrentUserResponse> | undefined,
    owner: SimpleEntityOwner | undefined
) => {
    const [corporationId, setCorporationId] = useState<string | undefined>();

    const canEditOwnedEntity = useMemo(() => {
        const isSaasOrSentry =
            user?.ApplicationRole === ApplicationRoleCode.SystemAdmin ||
            user?.ApplicationRole === ApplicationRoleCode.Sentry;

        if (owner) {
            switch (owner.type) {
                case EdgeComputeOwnerType.Corporation:
                    return isSaasOrSentry || hasCorporationAdminRole(user, owner.id);
                case EdgeComputeOwnerType.Location:
                    return (
                        isSaasOrSentry ||
                        hasLocationAdminRole(user, owner.id) ||
                        hasCorporationAdminRole(user, corporationId)
                    );
                default:
                    return isSaasOrSentry;
            }
        }
        return false;
    }, [corporationId, owner, user]);

    useEffect(() => {
        if (owner && owner.type === EdgeComputeOwnerType.Location) {
            sdClient
                .getLocation({ locationId: owner.id })
                .then((result) => setCorporationId(result.Corporation.CorporationId))
                .catch((error: unknown) => {
                    const isNetworkError =
                        error instanceof TypeError &&
                        (error.message.includes('fetch') ||
                            error.message.includes('Load failed'));

                    if (isNetworkError) {
                        sentryAddBreadcrumb({
                            category: 'permissions.error',
                            message: 'Failed to fetch location for permission check',
                            level: 'warning',
                            data: { locationId: owner.id, error: String(error) },
                        });
                    } else {
                        // Non-network errors (403, 500, auth) should be visible in Sentry
                        sentryCaptureMessage({
                            message: 'Failed to fetch location for permission check',
                            level: 'error',
                            context: {
                                permissionCheck: {
                                    locationId: owner.id,
                                    error: String(error),
                                },
                            },
                        });
                    }
                });
        } else {
            setCorporationId(undefined);
        }
    }, [owner]);

    return canEditOwnedEntity;
};

type UseOrganizationPermissionsReturn = Readonly<{
    adminCorporationIds: Set<string> | undefined;
    adminLocationIds: Set<string> | undefined;
}>;

const useOrganizationPermissions = (): UseOrganizationPermissionsReturn & {
    user: ICurrentUserResponse | undefined;
} => {
    const { user } = useUser();

    return { ...useOrganizationPermissionsForUser({ user }), user };
};

export const getOrganizationPermissionsForUser = (
    user: ICurrentUserResponse | IUserResponse | undefined
): UseOrganizationPermissionsReturn => {
    const corporations = user?.Corporations.filter(
        (corporation) => corporation.CorporationUserRole === CorporationUserRole.Admin
    ).map(({ CorporationId }) => CorporationId);
    const currentUserAdminCorporationIds = corporations
        ? new Set(corporations)
        : undefined;

    const locations = user?.Locations.filter(
        (location) => location.LocationUserRole === LocationUserRole.Admin
    ).map(({ LocationId }) => LocationId);
    const currentUserAdminLocationIds = locations ? new Set(locations) : undefined;

    return {
        adminCorporationIds: currentUserAdminCorporationIds,
        adminLocationIds: currentUserAdminLocationIds,
    };
};

export const useOrganizationPermissionsForUser = ({
    user,
}: {
    user: ICurrentUserResponse | IUserResponse | undefined;
}): UseOrganizationPermissionsReturn =>
    useMemo(() => getOrganizationPermissionsForUser(user), [user]);

export const usePermissionsCheck = <T>(
    checkFn: (
        resource: T,
        userRole: ApplicationRoleCode,
        adminCorporationIds: Set<string>,
        adminLocationIds: Set<string>
    ) => boolean
) => {
    const { user, adminCorporationIds, adminLocationIds } = useOrganizationPermissions();

    return {
        check: useMemo(
            () =>
                user && adminCorporationIds && adminLocationIds
                    ? (resource: T) =>
                          checkFn(
                              resource,
                              user.ApplicationRole,
                              adminCorporationIds,
                              adminLocationIds
                          )
                    : () => false,
            [checkFn, user, adminCorporationIds, adminLocationIds]
        ),
    };
};
